import type { HttpRequest } from "../network/types.ts";
import { resolveUrl } from "./UrlResolver.ts";
import {
  ExtractionError,
  type OperationContext,
  type RecordValue,
  type SourceProfile,
  type SourceStep,
} from "./types.ts";

export function readPath(value: unknown, path: string): unknown {
  if (!path) return value;
  return path.split(".").reduce<unknown>((current, part) => {
    if (["__proto__", "prototype", "constructor"].includes(part))
      throw new ExtractionError("INVALID_PROFILE", "Ruta de propiedad prohibida.");
    return current !== null && typeof current === "object" && Object.hasOwn(current, part)
      ? (current as Record<string, unknown>)[part]
      : undefined;
  }, value);
}
export function interpolate(
  template: string,
  variables: unknown,
  mode: "url" | "text" = "text",
): string {
  return template.replace(/\{([^{}]+)\}/g, (_, key: string) => {
    const value = readPath(variables, key);
    if (value === undefined || value === null || typeof value === "object")
      throw new ExtractionError("MISSING_VARIABLE", `Variable ausente o no escalar: ${key}`);
    let text = String(value);
    if ((key === "baseUrl" || key.endsWith(".url")) && template.includes(`{${key}}/`))
      text = text.replace(/\/+$/, "");
    // Entire URL variables remain URLs; search terms and IDs are encoded exactly once.
    const fullUrl = template === `{${key}}` && /^https?:\/\//i.test(text);
    return mode === "url" && !fullUrl && key !== "baseUrl" && !key.endsWith(".url")
      ? encodeURIComponent(text)
      : text;
  });
}
export function buildRequest(
  profile: SourceProfile,
  step: SourceStep,
  context: OperationContext,
  prev: RecordValue,
  base: string,
): HttpRequest & { responseType: "text" } {
  const variables = { ...context, baseUrl: profile.baseUrl, prev };
  let url = resolveUrl(interpolate(step.url, variables, "url"), base, profile);
  // WordPress redirects paged=1 to its canonical first-page URL.
  if(profile.template==='Madara'&&(!step.method||step.method==='GET')){
    const canonical=new URL(url);
    if(canonical.searchParams.get('paged')==='1'){canonical.searchParams.delete('paged');url=canonical.href;}
  }
  const headers: Record<string, string> = {};
  for (const [name, value] of Object.entries(step.headers ?? {})) {
    const resolved = interpolate(value, variables);
    if (/\r|\n/.test(resolved))
      throw new ExtractionError("INVALID_PROFILE", "Cabecera con salto de línea.");
    headers[name] = resolved;
  }
  if (step.referer)
    headers.Referer = resolveUrl(interpolate(step.referer, variables, "url"), base, profile);
  for (const [key, value] of Object.entries(headers))
    if (/^(referer|origin)$/i.test(key)) headers[key] = resolveUrl(value, base, profile);
  let body: string | undefined;
  if (step.form) {
    body = new URLSearchParams(
      Object.entries(step.form).map(([key, value]) => [key, interpolate(value, variables)]),
    ).toString();
    headers["Content-Type"] = "application/x-www-form-urlencoded";
  }
  return {
    sourceId: profile.id,
    url,
    method: step.method ?? "GET",
    headers,
    body,
    responseType: "text",
    credentials: profile.requiresSession ? "include" : "omit",
    cache: {
      ttlMs: step.form ? 0 : (profile.cacheTtlMs ?? 300000),
      scope: profile.requiresSession ? "session" : "public",
    },
    signal: context.signal,
    corsOpen: profile.corsOpen,
    requiresSession: profile.requiresSession,
  };
}
